import {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
  Fragment,
} from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'

import { TermsAndConditionsModal } from '../../components/TermsAndConditionsModal'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useAuthContext } from '../../contexts/AuthContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { useScreeningData } from '@/modules/Screening/contexts/screeningContext'

import services from '@/modules/Auth/auth-services'
import themeServices from '@/theme/theme.services'
import { setCache, getCache, resetCache } from '@/utils/cache'

import type { ClinicAndFlags, IUser } from '../../auth-types'
import LoadingPage from '@/components/LoadingPage'
import redirectToOldApplication from '@/utils/redirectToOldApplication'
import { LOGGED_IN_WITH_MAGIC_LINK } from './consts'
import { RenderIf } from '@/components/RenderIf'
import { MagicLinkParams } from './types'
import { toast } from '@conexasaude/conexa-hero'
import { MinorsTermModal } from '../../components/MinorsTermModal'
import { showErrorToast } from '@/modules/Activation/utils/requisitionErrors'
import { useNavigation } from '@/hooks/useNavigation'
import { IS_PRODUCTION } from '@/constants'

const MagicLink: FunctionComponent = () => {
  const [isLoading, setIsLoading] = useState(true)
  const history = useHistory()
  const location = useLocation()
  const currentPath = location.pathname
  const { token, redirectTo } = useParams<MagicLinkParams>()
  const [parsedRedirect, setParsedRedirect] = useState(null)
  const [enableMultiprofileRedirection, setEnableMultiprofileRedirection] =
    useState(false)
  const { signIn, choiceUser, setUserClinic, signOut } = useAuthContext()
  const { setThemeColors } = useThemeGeneratorContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()
  const { showModal } = useModalContext()
  const navigate = useNavigation()

  const { embed } = useThemeGeneratorContext()
  const { setScreeningData } = useScreeningData()
  const directLogin = useCallback(async () => {
    const userData = await services.getUserData()
    if (userData?.user?.blocked) {
      signOut(true)
      history.push('/login')
      return
    }
    const userToken = getCache<string>('token')
    let users = await services.magicLinkLogin(token)
    checkMultiprofileRedirection(users)

    const userClinic: ClinicAndFlags = await services.getClinic(userToken)
    if (!userClinic?.flags?.NovaExperiencia) {
      redirectToOldApplication()
      return
    }

    if (userClinic?.flags?.PossuiAplicativoProprio) {
      const filteredUsers = [...users].filter(
        user => user.usuario.clinica.id === userClinic.clinic.idClinica,
      )

      users = filteredUsers
    }

    const clinicColors = await themeServices.getClinicColorsById(
      userClinic.clinic.idClinica,
    )

    const ip = (await services.getUserIP()) || ''
    const termsAcceptance = await services.checkTermsAcceptance()
    const selectedUser = users?.find(u => u?.usuario?.id === userData?.user?.id)
    signIn(users)
    choiceUser(userData?.user?.id, ip)

    if (!termsAcceptance) return
    setThemeColors(clinicColors)
    setUserClinic(userClinic)
    setSpecialities(userClinic?.clinic)

    setCache(LOGGED_IN_WITH_MAGIC_LINK, new Date().getTime())
    if (!selectedUser?.usuario?.flagAcessou)
      await services.validateFirstAccess(userToken)
  }, [
    choiceUser,
    history,
    setSpecialities,
    setThemeColors,
    setUserClinic,
    signIn,
    signOut,
    token,
  ])

  const checkMultiprofileRedirection = (users: IUser[]) => {
    const redirectToMultiprofile = users?.find(
      user =>
        user?.usuario?.paciente?.titularidade === 'TITULAR' && user?.frontCnx,
    )

    setEnableMultiprofileRedirection(!!redirectToMultiprofile)
    return !!redirectToMultiprofile
  }

  const resolveSpecilityLength = (url: string) => {
    let specialityId = ''
    if (url.split('/').length === 2) specialityId = url.split('/')[1]
    else specialityId = url.split('/')[1] + '_' + url.split('/')[2]

    setScreeningData(screening => {
      screening.professionalType = specialityId
      return screening
    })
  }
  const redirectToEmbed = (url: string) => {
    let parsedUrl = `/${url}`

    if (embed.full) {
      parsedUrl = `${parsedUrl}?embedFull=true`
    }

    setParsedRedirect(parsedUrl)
    history.push(parsedUrl)
  }

  const compoundSpecialities = ['EDUCADOR_FISICO', 'ASSISTENTE_SOCIAL']

  useEffect(() => {
    const checkTokenValidity = async () => {
      resetCache()
      try {
        let termsAcceptance = null
        let minorsTermAcceptance = null
        setIsLoading(true)

        if (currentPath?.includes('confirmar-via-email')) {
          const userToken = currentPath
            ?.split('/navegador/')?.[1]
            ?.split('/')?.[0]
          const attendanceToken = currentPath?.split(
            '/confirmar-via-email_',
          )?.[1]

          setIsLoading(false)
          return navigate(
            `/confirmando-sua-consulta/${userToken}/${attendanceToken}`,
          )
        }
        if (!currentPath?.includes('multilogin')) {
          const userToken = await services.authMagicLink(token)
          setCache('token', userToken)
          termsAcceptance = await services.checkTermsAcceptance()

          minorsTermAcceptance = await services.checkMinorsTermAcceptance()
          if (!minorsTermAcceptance) {
            showModal('MINORS_TERM', {
              title: 'Termo para crianças e adolescentes',
              width: 480,
              persistent: true,
            })
            return
          }

          await directLogin()
          setIsLoading(false)
        } else {
          const users = await services.magicLinkLoginMultiprofile(token)
          signIn(users)
          setIsLoading(false)
          history.push('/escolha-unificada')
          return
        }

        if (redirectTo) {
          let formattedRedirectTo = ''
          formattedRedirectTo = redirectTo?.replace(/_/g, '/')
          if (compoundSpecialities?.some(spec => redirectTo?.includes(spec)))
            compoundSpecialities?.forEach(speciality => {
              const specialityWithSlash = speciality?.replace('_', '/')
              formattedRedirectTo = formattedRedirectTo?.replace(
                specialityWithSlash,
                speciality,
              )
            })

          if (formattedRedirectTo === 'home/atendimento/imediato') {
            setCache('scheduledHidden', true)
            setParsedRedirect('/')
            if (termsAcceptance) {
              redirectToEmbed('')
              return
            }
          } else if (formattedRedirectTo.includes('atendimento-imediato/')) {
            resolveSpecilityLength(formattedRedirectTo)
            setParsedRedirect('triagem')
            if (termsAcceptance) {
              redirectToEmbed('triagem')
              return
            }
          } else if (formattedRedirectTo.includes('especialidades')) {
            const professionalType = formattedRedirectTo
              .split('/')[1]
              .toLowerCase()

            setParsedRedirect(
              `atendimento-agendado/preferencia/${professionalType}/especialidades`,
            )

            if (termsAcceptance) {
              redirectToEmbed(
                `atendimento-agendado/preferencia/${professionalType}/especialidades`,
              )
              return
            }
          } else if (
            formattedRedirectTo.includes('atendimento-agendado') &&
            !formattedRedirectTo.includes('preferencia') &&
            !formattedRedirectTo.includes('categoria')
          ) {
            setParsedRedirect(`/${formattedRedirectTo}`)
            if (termsAcceptance) {
              redirectToEmbed('atendimento-agendado/validar/atendimento')
              return
            }
          } else if (formattedRedirectTo.includes('meus-atendimentos')) {
            const id = formattedRedirectTo.split('/')[2]
            const action = formattedRedirectTo.split('/')[3] || ''

            if (termsAcceptance) {
              if (!id) return redirectToEmbed('meus-atendimentos')

              if (id.length < 30) {
                return redirectToEmbed(
                  `meus-atendimentos/atendimento/${id}/${action}`,
                )
              } else {
                return redirectToEmbed(
                  `meus-atendimentos/atendimento/prefila/${id}/${action}`,
                )
              }
            }
          } else if (formattedRedirectTo.includes('minhas-reservas')) {
            const id = formattedRedirectTo.split('/')[2]

            return redirectToEmbed(`minhas-reservas/confirmar-reserva/${id}`)
          } else if (formattedRedirectTo.includes('signup-multiprofile')) {
            return redirectToEmbed('escolha')
          } else {
            setParsedRedirect(`/${formattedRedirectTo}`)
            if (termsAcceptance) history.push(`/${formattedRedirectTo}`)
          }
        } else {
          if (termsAcceptance) {
            if (enableMultiprofileRedirection) {
              setIsLoading(false)
              navigate('/escolha')
              return
            }
            setParsedRedirect('/')
            history.push('/')
          }
        }

        // else
        if (!termsAcceptance) {
          showModal('TERMS_AND_CONDITIONS', {
            title: 'Termos de uso',
            width: 455,
            persistent: true,
          })
        }
      } catch (error) {
        if (error?.toString()?.includes('403')) {
          if (IS_PRODUCTION)
            window.location.href = 'https://ativacao.conexasaude.com.br/'
          else window.location.href = 'https://qa-ativacao.conexasaude.com.br/'
        } else {
          const errorMessage =
            error?.response?.data?.msg ||
            error?.response?.data?.object?.msg ||
            error?.message

          toast({
            message: errorMessage,
            variant: 'error',
          })
          history.push('/')
        }
      }

      setIsLoading(false)
    }

    checkTokenValidity()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    token,
    redirectTo,
    showModal,
    directLogin,
    history,
    embed,
    enableMultiprofileRedirection,
  ])

  const checkTermsAndConditionsAcceptance = useCallback(async () => {
    try {
      const termsChecked = await services.checkTermsAcceptance()
      if (!termsChecked) {
        showModal('TERMS_AND_CONDITIONS', {
          title: 'Termos de uso',
          width: 455,
          persistent: true,
        })
      } else {
        directLogin()
      }
    } catch (error) {
      showErrorToast(error)
    }
  }, [directLogin, showModal])

  return (
    <Fragment>
      <RenderIf condition={isLoading}>
        <LoadingPage
          withoutNav
          text="Estamos deixando tudo pronto pra você!"
          useNeutralColor
        />
      </RenderIf>
      <TermsAndConditionsModal
        redirectTo={parsedRedirect}
        directLogin={directLogin}
        setLoading={setIsLoading}
        userToken={getCache<string>('token')}
      />
      <MinorsTermModal
        checkTermsAndConditionsAcceptance={checkTermsAndConditionsAcceptance}
      />
    </Fragment>
  )
}

export default MagicLink
